import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Microphone, VideoCamera, Gear, X, FilmStrip, Play } from '@phosphor-icons/react';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import { markCameraActive } from '../hooks/useEventCachePrefetch';
import { fetchOnce } from '../lib/eventCache';
import ReconnectingScreen from '../components/ReconnectingScreen';
import BackButton from '../components/BackButton';
import { useBoothCameraPipeline } from '../hooks/useBoothCameraPipeline';
import { useParticipationAudioPipeline } from '../hooks/useParticipationAudioPipeline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_BG = {
  wedding: { background: `linear-gradient(180deg, rgba(244,114,182,0.3) 0%, rgba(251,191,36,0.15) 50%, rgba(120,53,15,0.4) 100%), radial-gradient(circle at 30% 20%, rgba(251,191,36,0.3), transparent 50%), radial-gradient(circle at 70% 80%, rgba(244,114,182,0.2), transparent 50%), #2D1B2E` },
  party: { background: `linear-gradient(135deg, #0F0723, #1A0A3E, #0D0D0D)` },
  corporate: { background: 'linear-gradient(180deg, #F1F5F9, #E2E8F0)' },
};
const FONT_MAP = { script: "'Playfair Display', serif", sans: "'Inter', sans-serif", serif: "'Georgia', serif" };

// Sound effects
let audioCtx = null;
const playTone = (freq, dur, vol = 0.1) => {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const o = audioCtx.createOscillator(), g = audioCtx.createGain();
    o.connect(g); g.connect(audioCtx.destination);
    o.frequency.value = freq; g.gain.value = vol;
    o.start(); g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + dur);
    o.stop(audioCtx.currentTime + dur);
  } catch {}
};
const playTap = () => playTone(800, 0.08, 0.05);
const playStart = () => { playTone(523, 0.15, 0.08); setTimeout(() => playTone(659, 0.15, 0.08), 150); };
const playSuccess = () => { playTone(523, 0.2, 0.06); setTimeout(() => playTone(659, 0.2, 0.06), 200); setTimeout(() => playTone(784, 0.3, 0.06), 400); };

// Waveform visualizer — reads the PROCESSED audio stream from the
// participation audio pipeline so the live meter reflects the same
// signal that will be recorded (intelligibility-first, not raw).
const WaveformVisualizer = ({ stream, isRecording }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  useEffect(() => {
    if (!stream || !isRecording || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const src = ac.createMediaStreamSource(stream);
    const analyser = ac.createAnalyser(); analyser.fftSize = 256;
    src.connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const draw = () => {
      const w = canvasRef.current.width, h = canvasRef.current.height;
      analyser.getByteFrequencyData(buf); ctx.clearRect(0, 0, w, h);
      const bw = (w / buf.length) * 2.5; let x = 0;
      for (let i = 0; i < buf.length; i++) {
        const bh = (buf[i] / 255) * h * 0.8;
        const grad = ctx.createLinearGradient(0, h, 0, h - bh);
        grad.addColorStop(0, 'rgba(242,139,130,0.4)'); grad.addColorStop(1, 'rgba(242,139,130,0.9)');
        ctx.fillStyle = grad; ctx.fillRect(x, h - bh, bw - 1, bh); x += bw;
      }
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animRef.current); ac.close(); };
  }, [stream, isRecording]);
  return <canvas ref={canvasRef} width={300} height={120} className="rounded-xl" style={{ opacity: isRecording ? 1 : 0.3 }} />;
};

// ═══════════════════════════════════════
// MAIN GUESTBOOK RECORDER — iter206
// ─────────────────────────────────────
// Unified participation pipelines:
//   • audio path  → useParticipationAudioPipeline(profile:'voice_message')
//   • video path  → useBoothCameraPipeline(profile:'guestbook') for the
//                    VIDEO TRACK ONLY, composited with the processed
//                    audio track from the audio pipeline (camera
//                    profile.audio is false).
// This guarantees one emotional audio signature across audio-only
// and video guestbook modes — guests sound the SAME way.
// ═══════════════════════════════════════
const GuestbookRecorder = () => {
  const { slug } = useParams();
  const { event: recoveredEvent, status: recoveryStatus, retry: retryRecovery } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  const [event, setEvent] = useState(null);
  const [config, setConfig] = useState(null);
  const [screen, setScreen] = useState('home');
  const [countdown, setCountdown] = useState(30);
  const [uploading, setUploading] = useState(false);
  const [adminTaps, setAdminTaps] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [recordingCount, setRecordingCount] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [ctaGlow, setCtaGlow] = useState(false);

  // iter206 — unified pipelines. cam owns the VIDEO track only,
  // mic owns the (cleaned) audio track and recorded raw blob.
  const cam = useBoothCameraPipeline({ profile: 'guestbook' });
  const mic = useParticipationAudioPipeline({ profile: 'voice_message' });

  // Composite video recorder (built at surface level — NOT inside
  // either hook). The MediaStream is composed manually from the
  // camera's video track + the audio pipeline's processed track.
  const videoRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const videoRecMimeRef = useRef('');
  const compositeStreamRef = useRef(null);
  const timerRef = useRef(null);
  const modeRef = useRef(null); // 'audio' | 'video' — for handleUpload

  // Mark camera-active during MediaRecorder lifetime so the prefetch
  // layer pauses Tier-2 background work while we're eating CPU on
  // mic/camera. Cleared on unmount or any non-recording screen.
  useEffect(() => {
    const live = screen === 'recording_video' || screen === 'recording_audio';
    markCameraActive(live);
    return () => markCameraActive(false);
  }, [screen]);

  // Hydrate `event` from the recovery hook the moment it's ready,
  // then fan-out the auxiliary calls (screen-config, photos, preview).
  useEffect(() => {
    if (recoveryStatus !== 'ready' || !recoveredEvent?.event_id) return;
    let aborted = false;
    setEvent(recoveredEvent);
    const eid = recoveredEvent.event_id;
    (async () => {
      try {
        const c = await axios.get(`${API}/events/${eid}/screen-config`);
        if (!aborted) setConfig(c.data);
      } catch { if (!aborted) setConfig(null); }
      try {
        const photos = await fetchOnce(`${eid}:photos`, () =>
          axios.get(`${API}/events/${eid}/photos`).then((r) => r.data)
        );
        if (!aborted) setUploadCount(Array.isArray(photos) ? photos.length : 0);
      } catch { /* ignore */ }
      try {
        await fetchOnce(`${eid}:film-preview`, () =>
          axios.post(`${API}/events/${eid}/film/preview`).then((r) => r.data),
          { ttl: 300_000 }
        );
      } catch { /* ignore */ }
    })();
    return () => { aborted = true; };
  }, [recoveryStatus, recoveredEvent]);

  // CTA glow pulse every 10s
  useEffect(() => {
    if (screen !== 'upsell') return;
    const interval = setInterval(() => { setCtaGlow(true); setTimeout(() => setCtaGlow(false), 1500); }, 10000);
    const initial = setTimeout(() => { setCtaGlow(true); setTimeout(() => setCtaGlow(false), 1500); }, 2000);
    return () => { clearInterval(interval); clearTimeout(initial); };
  }, [screen]);

  const maxDuration = 30;

  // Cleanup on unmount — let both pipelines stop themselves; the
  // hooks already wire visibilitychange / pagehide / beforeunload.
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      try { videoRecorderRef.current?.stop?.(); } catch {}
      if (compositeStreamRef.current) {
        try { compositeStreamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
        compositeStreamRef.current = null;
      }
      cam.stop();
      mic.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const negotiateVideoMime = (candidates) => {
    if (typeof window === 'undefined' || !window.MediaRecorder) return '';
    for (const cand of candidates || []) {
      try { if (window.MediaRecorder.isTypeSupported(cand)) return cand; } catch {}
    }
    return '';
  };

  const startRecording = async (mode) => {
    if (!event?.event_id) {
      toast.error('Event still loading — please wait a moment.', { duration: 4000 });
      return;
    }
    playTap();
    modeRef.current = mode;
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        throw new Error('UnsupportedBrowser: navigator.mediaDevices.getUserMedia not available. Please open this link in Chrome or Safari over HTTPS.');
      }

      // ── AUDIO PIPELINE — always required (both modes) ─────
      const processedStream = await mic.start();
      if (!processedStream) {
        // mic.start surfaces its own friendly error via onError? No —
        // we read mic.error in the next tick. Fall back to a toast.
        toast.error(mic.error || 'Please allow microphone access, then try again.', { duration: 6000 });
        return;
      }

      // ── VIDEO PIPELINE — only for video mode. We need to mount
      //    the screen first so cam.videoRef is attached, OR start
      //    the stream without a videoRef and reattach via the
      //    pipeline's existing watcher. The pipeline DOES reattach
      //    in the effect that watches `status`, so it's safe to
      //    flip the screen now and start the camera concurrently.
      if (mode === 'video') {
        setScreen('recording_video');
        // Yield a frame so the <video ref={cam.videoRef}> mounts.
        await new Promise((r) => requestAnimationFrame(() => r()));
        const videoStream = await cam.start();
        if (!videoStream) {
          toast.error(cam.error || 'Please allow camera access, then try again.', { duration: 6000 });
          mic.stop();
          setScreen('home');
          return;
        }
      } else {
        setScreen('recording_audio');
      }

      // ── RECORDING ─────────────────────────────────────────
      if (mode === 'audio') {
        // Audio-only: delegate to the audio pipeline. The pipeline
        // owns its own MediaRecorder, raw blob, processed blob,
        // voicePresenceMoments, and rawRoomToneRMS.
        const ok = mic.startRecording();
        if (!ok) {
          toast.error('Recorder could not start. Try once more.', { duration: 5000 });
          mic.stop();
          setScreen('home');
          return;
        }
      } else {
        // Video mode: compose the final stream manually from the
        // camera's video track + the audio pipeline's processed
        // audio track. This is the architectural keystone — video
        // and audio modes share one emotional audio signature.
        const camStream = cam.streamRef.current;
        const audioStream = mic.processedStreamRef.current;
        if (!camStream || !audioStream) {
          toast.error('Capture initialised incompletely. Try again.', { duration: 5000 });
          cam.stop(); mic.stop();
          setScreen('home');
          return;
        }
        const videoTrack = camStream.getVideoTracks()[0];
        const audioTrack = audioStream.getAudioTracks()[0];
        if (!videoTrack || !audioTrack) {
          toast.error('Couldn’t open both camera and microphone. Try again.', { duration: 5000 });
          cam.stop(); mic.stop();
          setScreen('home');
          return;
        }
        const composite = new MediaStream([videoTrack, audioTrack]);
        compositeStreamRef.current = composite;

        // Start the audio pipeline's recorder IN PARALLEL so we
        // preserve a clean rawBlob + processedBlob + presence
        // moments + rawRoomToneRMS for the video too. (Even though
        // the composite video carries audio inside it, the raw
        // audio blob is the "archive truth" for future recap /
        // denoise passes — the user's iter206 directive.)
        mic.startRecording();

        // Composite MediaRecorder — honest mime negotiation.
        videoChunksRef.current = [];
        const candidates = cam.profile.videoMimeCandidates;
        const mime = negotiateVideoMime(candidates);
        videoRecMimeRef.current = mime;
        let rec;
        try {
          const opts = { ...(mime ? { mimeType: mime } : {}) };
          if (cam.profile.videoBitsPerSecond) opts.videoBitsPerSecond = cam.profile.videoBitsPerSecond;
          rec = new MediaRecorder(composite, opts);
        } catch {
          try { rec = new MediaRecorder(composite); }
          catch {
            toast.error("This browser can’t record video. Try Chrome or Safari.", { duration: 6000 });
            cam.stop(); mic.stop();
            setScreen('home');
            return;
          }
        }
        videoRecorderRef.current = rec;
        rec.ondataavailable = (e) => { if (e.data?.size) videoChunksRef.current.push(e.data); };
        rec.onstop = async () => {
          const recMime = rec.mimeType || mime || 'video/webm';
          const ext = recMime.includes('mp4') ? 'mp4' : 'webm';
          const videoBlob = new Blob(videoChunksRef.current, { type: recMime });
          // Stop the audio pipeline's recorder — gives us rawBlob,
          // processedBlob, rawRoomToneRMS, voicePresenceMoments.
          const audio = await mic.stopRecording();
          handleUpload({
            primaryBlob: videoBlob,
            primaryMime: recMime,
            ext,
            rawAudioBlob: audio?.rawBlob || null,
            rawAudioMime: audio?.mime || '',
            rawRoomToneRMS: audio?.rawRoomToneRMS ?? mic.rawRoomToneRMS,
            voicePresenceMoments: audio?.voicePresenceMoments || mic.voicePresenceMoments,
            mode: 'video',
          });
        };
        try { rec.start(250); }
        catch {
          toast.error('Could not start video recording.', { duration: 5000 });
          cam.stop(); mic.stop();
          setScreen('home');
          return;
        }
      }

      setCountdown(maxDuration);
      playStart();
      timerRef.current = setInterval(() => {
        setCountdown(prev => { if (prev <= 1) { stopRecording(); return 0; } return prev - 1; });
      }, 1000);
    } catch (err) {
      console.error('[Guestbook] startRecording crashed', err);
      toast.error(err?.message?.startsWith('UnsupportedBrowser')
        ? 'This browser cannot record audio/video. Please open the link in Chrome or Safari.'
        : `Could not start ${mode} — ${err?.message || 'unknown error'}.`, { duration: 6000 });
      cam.stop();
      mic.stop();
    }
  };

  const stopRecording = useCallback(async () => {
    clearInterval(timerRef.current);
    if (modeRef.current === 'video') {
      // The composite recorder's onstop drives the rest of the flow
      // (including stopping the audio pipeline + handleUpload).
      try { videoRecorderRef.current?.stop?.(); }
      catch { cam.stop(); mic.stop(); }
    } else {
      // Audio-only — pull both blobs + moments from the audio pipeline.
      const audio = await mic.stopRecording();
      mic.stop();
      handleUpload({
        primaryBlob: audio?.processedBlob || audio?.rawBlob,
        primaryMime: audio?.processedBlob?.type || audio?.mime || 'audio/webm',
        ext: audio?.ext || 'webm',
        rawAudioBlob: audio?.rawBlob || null,
        rawAudioMime: audio?.mime || '',
        rawRoomToneRMS: audio?.rawRoomToneRMS ?? mic.rawRoomToneRMS,
        voicePresenceMoments: audio?.voicePresenceMoments || mic.voicePresenceMoments,
        mode: 'audio',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async ({
    primaryBlob,
    primaryMime,
    ext,
    rawAudioBlob,
    rawAudioMime,
    rawRoomToneRMS,
    voicePresenceMoments,
    mode,
  }) => {
    // Camera no longer needed once recording stops.
    if (mode === 'video') cam.stop();

    setUploading(true);
    setScreen('success');
    setShowConfetti(true);
    playSuccess();
    setTimeout(() => setShowConfetti(false), 2000);

    try {
      if (!primaryBlob) throw new Error('empty recording');
      const file = new File([primaryBlob], `guestbook_${mode}_${Date.now()}.${ext}`, { type: primaryMime });
      const fd = new FormData(); fd.append('file', file);
      // iter206 — attach raw audio (room truth) when present, so
      // the server can keep it alongside the processed take for
      // future recap / denoise / archive mastering.
      if (rawAudioBlob && mode === 'audio' && rawAudioBlob !== primaryBlob) {
        const rawExt = rawAudioMime.includes('mp4') ? 'm4a' : 'webm';
        fd.append('raw', new File([rawAudioBlob], `guestbook_raw_${Date.now()}.${rawExt}`, { type: rawAudioMime }));
      }
      if (rawAudioBlob && mode === 'video') {
        const rawExt = rawAudioMime.includes('mp4') ? 'm4a' : 'webm';
        fd.append('raw_audio', new File([rawAudioBlob], `guestbook_audio_${Date.now()}.${rawExt}`, { type: rawAudioMime }));
      }
      // Attach participation telemetry as JSON sidecars — the server
      // may ignore them today, but every upload carries the room
      // signature for downstream Event Film ✦ + denoise systems.
      if (typeof rawRoomToneRMS === 'number') {
        fd.append('room_tone_rms', String(rawRoomToneRMS));
      }
      if (Array.isArray(voicePresenceMoments) && voicePresenceMoments.length) {
        try { fd.append('voice_presence_moments', JSON.stringify(voicePresenceMoments)); } catch {}
      }
      await axios.post(`${API}/events/${event.event_id}/photos?source=guestbook`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadCount(prev => prev + 1);
      setRecordingCount(prev => prev + 1);
      try { window.dispatchEvent(new CustomEvent('eventdrop:video_guestbook_submit')); } catch {}
      try {
        const { markFeatureComplete } = await import('../lib/features');
        markFeatureComplete(slug, 'video_guestbook');
        window.dispatchEvent(new CustomEvent('eventdrop:completion_tick'));
      } catch {}
    } catch (uploadErr) {
      console.error('[Guestbook] upload failed', uploadErr);
    }
    finally {
      setUploading(false);
      setTimeout(() => {
        const total = uploadCount + 1;
        const recs = recordingCount + 1;
        const filmOwned = event?.film_purchased || event?.package_type === 'premium';
        if (!filmOwned && (total >= 5 || recs >= 2)) {
          setPreviewUrl(`${BACKEND_URL}/api/events/${event.event_id}/film/preview-stream`);
          setScreen('upsell');
        } else {
          setScreen('home');
        }
      }, 2000);
    }
  };

  const handleAdminTap = () => {
    const t = adminTaps + 1; setAdminTaps(t);
    if (t >= 5) { setShowSettings(true); setAdminTaps(0); }
    setTimeout(() => setAdminTaps(0), 2000);
  };

  const getBgStyle = () => {
    if (!config) return PRESET_BG.wedding;
    if (config.bg_type === 'upload' && config.bg_value) return { backgroundImage: `url(${BACKEND_URL}${config.bg_value})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    if (config.bg_type === 'color' && config.bg_value) return { background: config.bg_value };
    return PRESET_BG[config.bg_value] || PRESET_BG.wedding;
  };

  const titleText = config?.title || 'Leave a Message';
  const subtitleText = config?.subtitle || 'Tap to leave a 30 second message!';
  const titleFont = FONT_MAP[config?.title_font] || FONT_MAP.script;
  const titleColor = config?.title_color || '#FFFFFF';
  const subtitleColor = config?.subtitle_color || '#FFFFFF';
  const audioBtnColor = config?.btn_audio_color || '#F28B82';
  const videoBtnColor = config?.btn_video_color || '#26A69A';
  const audioLabel = config?.btn_audio_label || 'Record Audio';
  const videoLabel = config?.btn_video_label || 'Record Video';

  if (screen === 'error' || recoveryStatus === 'failed') return (
    <ReconnectingScreen
      status="failed"
      onRetry={() => { setScreen('home'); retryRecovery(); }}
      dashboardHref="/dashboard"
      slug={slug}
      activeEvent={activeEvent}
    />
  );
  if (!event) return (
    <ReconnectingScreen
      status={recoveryStatus === 'restoring' ? 'restoring' : 'reconnecting'}
      slug={slug}
      activeEvent={activeEvent}
    />
  );

  return (
    <div className="fixed inset-0 overflow-hidden select-none" style={getBgStyle()} data-testid="guestbook-recorder">
      <div className="absolute inset-0 bg-black/25" />

      {screen === 'home' && (
        <div className="absolute top-5 left-5 z-30">
          <BackButton
            fallbackHref={`/event/${slug}`}
            label="Back"
            variant="subtle"
            testId="gb-back-btn"
          />
        </div>
      )}

      {showConfetti && (
        <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden" data-testid="gb-confetti">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className="gb-confetti-particle" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 0.8}s`,
              background: ['#F28B82', '#26A69A', '#FFD700', '#FF7A18', '#B89762'][i % 5],
            }} />
          ))}
        </div>
      )}

      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-6">

        {/* ═══ HOME ═══ */}
        {screen === 'home' && (
          <div className="flex flex-col items-center gb-anim-in" data-testid="gb-home-screen">
            <h1 className="text-center mb-3 leading-tight" style={{ fontFamily: titleFont, fontSize: 'clamp(40px, 8vw, 64px)', fontWeight: 700, color: titleColor, textShadow: '0 3px 12px rgba(0,0,0,0.4)' }} data-testid="gb-title">
              {titleText}
            </h1>
            <div className="flex gap-5 mt-8">
              <button onClick={() => startRecording('audio')} className="gb-btn group" style={{ minWidth: 140, minHeight: 140, background: audioBtnColor, boxShadow: `0 16px 40px ${audioBtnColor}55, 0 0 0 1px rgba(255,255,255,0.1) inset` }} data-testid="gb-record-audio-btn">
                <Microphone size={52} weight="bold" className="text-white mb-2 group-active:scale-90 transition-transform" />
                <span className="text-white text-base font-bold" style={{ letterSpacing: '-0.01em' }}>{audioLabel}</span>
              </button>
              <button onClick={() => startRecording('video')} className="gb-btn group" style={{ minWidth: 140, minHeight: 140, background: videoBtnColor, boxShadow: `0 16px 40px ${videoBtnColor}55, 0 0 0 1px rgba(255,255,255,0.1) inset` }} data-testid="gb-record-video-btn">
                <VideoCamera size={52} weight="bold" className="text-white mb-2 group-active:scale-90 transition-transform" />
                <span className="text-white text-base font-bold" style={{ letterSpacing: '-0.01em' }}>{videoLabel}</span>
              </button>
            </div>
            <p className="mt-8 text-center" style={{ color: subtitleColor, textShadow: '0 1px 6px rgba(0,0,0,0.4)', fontSize: '17px', fontWeight: 500 }} data-testid="gb-subtitle">{subtitleText}</p>
            {config?.show_settings_btn !== false && (
              <button onClick={handleAdminTap} className="absolute bottom-6 right-6 flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/20 backdrop-blur-sm border border-white/10" data-testid="gb-settings-trigger">
                <Gear size={14} weight="bold" className="text-white/60" /><span className="text-white/60 text-xs font-medium">Settings</span>
              </button>
            )}
          </div>
        )}

        {/* ═══ RECORDING VIDEO ═══ */}
        {screen === 'recording_video' && (
          <div className="w-full h-full flex flex-col items-center justify-center gb-anim-in" data-testid="gb-recording-video">
            <div className="relative w-full max-w-lg aspect-[4/3] rounded-3xl overflow-hidden mb-6" style={{ boxShadow: '0 0 0 4px rgba(255,59,92,0.7), 0 0 60px rgba(255,59,92,0.35)' }}>
              <video ref={cam.videoRef} autoPlay playsInline muted style={cam.previewStyle} data-testid="gb-camera-preview" />
              {/* REC pip */}
              <div className="absolute top-4 left-4 flex items-center gap-2.5 px-4 py-2.5 rounded-full" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span style={{ width: 16, height: 16, borderRadius: 999, background: '#FF3B5C', boxShadow: '0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset', animation: 'gbRecPulse 1.1s ease-in-out infinite' }} />
                <span className="text-white font-extrabold" style={{ fontSize: 14, letterSpacing: '0.14em' }}>REC</span>
              </div>
            </div>
            <div className="text-center mb-5" style={{ background: 'rgba(0,0,0,0.7)', padding: '12px 24px', borderRadius: 999, backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}>
              <span className="block text-white tabular-nums" style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-0.02em', textShadow: '0 2px 12px rgba(0,0,0,0.5)', lineHeight: 1 }} data-testid="gb-countdown">{countdown}</span>
              <p className="text-white/85 text-sm font-semibold mt-1" style={{ letterSpacing: '0.04em' }}>seconds remaining</p>
            </div>
            <button onClick={stopRecording} className="flex items-center justify-center gap-3 transition-all active:scale-95" style={{ minHeight: 72, padding: '0 36px', borderRadius: 999, background: 'linear-gradient(135deg, #FF3B5C 0%, #FF0099 100%)', color: '#FFFFFF', border: 'none', fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', boxShadow: '0 16px 40px rgba(255,59,92,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset', cursor: 'pointer' }} data-testid="gb-stop-btn">
              <span style={{ width: 16, height: 16, borderRadius: 4, background: '#FFFFFF' }} />
              Stop &amp; save
            </button>
          </div>
        )}

        {/* ═══ RECORDING AUDIO ═══ */}
        {screen === 'recording_audio' && (
          <div className="flex flex-col items-center justify-center gb-anim-in" data-testid="gb-recording-audio">
            <div className="relative mb-6">
              <div className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ background: audioBtnColor }} />
              <div className="w-32 h-32 rounded-full flex items-center justify-center" style={{ background: audioBtnColor, boxShadow: `0 0 60px ${audioBtnColor}80, 0 0 0 2px rgba(255,255,255,0.15) inset` }}>
                <Microphone size={64} weight="bold" className="text-white" />
              </div>
            </div>
            <div className="mb-6"><WaveformVisualizer stream={mic.processedStreamRef.current} isRecording={screen === 'recording_audio'} /></div>
            <div className="text-center mb-6" style={{ background: 'rgba(0,0,0,0.7)', padding: '12px 24px', borderRadius: 999, backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}>
              <span className="block text-white tabular-nums" style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-0.02em', textShadow: '0 2px 12px rgba(0,0,0,0.5)', lineHeight: 1 }} data-testid="gb-countdown">{countdown}</span>
              <p className="text-white/85 text-sm font-semibold mt-1" style={{ letterSpacing: '0.04em' }}>seconds remaining</p>
            </div>
            <button onClick={stopRecording} className="flex items-center justify-center gap-3 transition-all active:scale-95" style={{ minHeight: 72, padding: '0 36px', borderRadius: 999, background: 'linear-gradient(135deg, #FF3B5C 0%, #FF0099 100%)', color: '#FFFFFF', border: 'none', fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', boxShadow: '0 16px 40px rgba(255,59,92,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset', cursor: 'pointer' }} data-testid="gb-stop-btn">
              <span style={{ width: 16, height: 16, borderRadius: 4, background: '#FFFFFF' }} />
              Stop &amp; save
            </button>
          </div>
        )}

        {/* ═══ SUCCESS (2s) ═══ */}
        {screen === 'success' && (
          <div className="flex flex-col items-center justify-center gb-anim-in" data-testid="gb-success-screen">
            <div className="w-24 h-24 rounded-full bg-[#15161B]/15 backdrop-blur-md flex items-center justify-center mb-6 border border-white/20 gb-scale-in">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none"><path d="M14 24L22 32L34 16" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <h2 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: titleFont, textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>Message Saved</h2>
            <p className="text-white/70 text-lg">Thank you! Your message has been added</p>
            {uploading && <p className="text-white/50 text-sm mt-2">Uploading...</p>}
          </div>
        )}

        {/* ═══ FILM UPSELL ═══ */}
        {screen === 'upsell' && (
          <div className="flex flex-col items-center justify-center gb-anim-in max-w-lg px-4 w-full" data-testid="gb-film-upsell">
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.5) 100%)' }} />

            <div className="relative z-10 flex flex-col items-center w-full">
              <div className="w-full rounded-2xl overflow-hidden mb-6 shadow-2xl" style={{ maxWidth: 420 }} data-testid="upsell-preview">
                <video
                  src={previewUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full aspect-video object-cover"
                  style={{ filter: 'contrast(1.05) saturate(1.1)' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                  data-testid="upsell-preview-video"
                />
                <div className="w-full aspect-video flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a0525, #0d0d0d, #1a0a1e)', display: previewUrl ? 'none' : 'flex' }}>
                  <FilmStrip size={64} weight="thin" className="text-white/10" />
                </div>
              </div>

              <h2 className="text-3xl font-bold text-white text-center mb-2 leading-tight" style={{ fontFamily: titleFont, textShadow: '0 2px 12px rgba(0,0,0,0.4)' }} data-testid="upsell-headline">
                That moment is now part of your event story
              </h2>

              <p className="text-white/50 text-base text-center mb-1">Turn all these moments into one beautiful film</p>
              <p className="text-white/40 text-sm text-center mb-6" data-testid="upsell-content-count">
                {uploadCount >= 100 ? (
                  <><span className="text-[#B89762] font-semibold">{uploadCount}+</span> moments captured — ready to relive?</>
                ) : (
                  <><span className="text-[#B89762] font-semibold">{uploadCount}</span> moments captured so far</>
                )}
              </p>

              <div className="grid grid-cols-2 gap-2 mb-6 w-full" style={{ maxWidth: 380 }}>
                {['All guest moments combined', 'One beautiful story', 'Background music', 'Share instantly'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-white/70 text-sm">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #FF7A18, #FF3D77)' }}>
                      <Play size={6} weight="fill" className="text-white" />
                    </div>
                    {f}
                  </div>
                ))}
              </div>

              <button
                onClick={() => { playTap(); window.open(`${window.location.origin}/event/${slug}`, '_blank'); setScreen('home'); }}
                className={`w-full py-4 rounded-2xl font-bold text-white text-lg transition-all hover:scale-[1.02] mb-3 ${ctaGlow ? 'gb-cta-glow' : ''}`}
                style={{ background: 'linear-gradient(135deg, #FF7A18, #FF3D77)', boxShadow: ctaGlow ? '0 0 40px rgba(108,92,231,0.4), 0 0 80px rgba(62,198,231,0.2)' : '0 8px 32px rgba(108,92,231,0.25)', maxWidth: 380 }}
                data-testid="upsell-cta-btn"
              >
                Create Your Film — £29
              </button>

              <button onClick={() => { playTap(); setScreen('home'); }} className="text-white/30 text-sm font-medium hover:text-white/50 transition-colors py-2" data-testid="upsell-skip-btn">
                Maybe later
              </button>

              <p className="text-white/20 text-xs mt-3 text-center">Only takes 5–10 minutes to generate</p>
            </div>
          </div>
        )}
      </div>

      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowSettings(false)}>
          <div className="bg-[#15161B] rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="gb-settings-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg text-white">Settings</h3>
              <button onClick={() => setShowSettings(false)} className="p-2 rounded-full hover:bg-white/5"><X size={20} weight="bold" /></button>
            </div>
            <div className="space-y-4 text-sm text-gray-400">
              <div className="p-4 rounded-xl bg-white/5"><p className="font-semibold text-white mb-1">Event</p><p>{event.event_name}</p></div>
              <div className="p-4 rounded-xl bg-white/5"><p className="font-semibold text-white mb-1">Recording Duration</p><p>{maxDuration} seconds</p></div>
              <div className="p-4 rounded-xl bg-white/5"><p className="font-semibold text-white mb-1">Customise Screen</p><p>Go to Admin Panel → Design & Branding.</p></div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .gb-btn { border-radius:28px; display:flex; flex-direction:column; align-items:center; justify-content:center; transition:transform 0.15s ease, box-shadow 0.2s ease; cursor:pointer; border:none; outline:none; padding:12px; }
        .gb-btn:active { transform:scale(0.93); }
        .gb-btn:hover { transform:scale(1.03); }
        .gb-anim-in { animation: gbFadeIn 0.4s ease-out; }
        @keyframes gbFadeIn { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }
        .gb-scale-in { animation: gbScaleIn 0.5s cubic-bezier(0.34,1.56,0.64,1); }
        @keyframes gbScaleIn { from { transform:scale(0); } to { transform:scale(1); } }
        .gb-cta-glow { animation: gbGlow 1.5s ease-in-out; }
        @keyframes gbGlow { 0%,100% { box-shadow: 0 8px 32px rgba(108,92,231,0.25); } 50% { box-shadow: 0 0 50px rgba(108,92,231,0.5), 0 0 100px rgba(62,198,231,0.3); } }
        .gb-confetti-particle { position:absolute; top:-10px; width:8px; height:8px; border-radius:2px; animation: gbConfettiFall 2s ease-out forwards; }
        @keyframes gbConfettiFall { 0% { transform:translateY(0) rotate(0deg); opacity:1; } 100% { transform:translateY(100vh) rotate(720deg); opacity:0; } }
        @keyframes gbRecPulse {
          0%, 100% { box-shadow: 0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1); }
          50% { box-shadow: 0 0 28px rgba(255,59,92,1), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};

export default GuestbookRecorder;
